import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { randomize, type BoxInfo, type CardInfo } from '@lub0v/santa-utils';
import {
  GifteePair,
  Note,
  Select,
  Stack,
  Typography,
  showDialog,
  showToast,
} from '@src/components';
import { santaClient } from '@src/state/client';

function Assignment({
  card,
  cards,
  onChange,
}: {
  card: CardInfo;
  cards: CardInfo[];
  onChange: (id: number) => void;
}) {
  const { t } = useTranslation();
  const [wardId, setWardId] = useState('');
  // Insert the newcomer into an existing pair, exactly as in the legacy flow.
  const options = cards.filter(
    (person) => person.id !== card.id && cards.some((other) => other.wardId === person.id),
  );
  const giftee = cards.find((person) => String(person.id) === wardId);
  const santa = cards.find((person) => String(person.wardId) === wardId);
  return (
    <Stack gap={4}>
      <Typography as="p" role="body/sm">
        {t('game.people.assignmentIntro')}
      </Typography>
      {options.length ? (
        <Select
          value={wardId}
          options={options.map((person) => ({ value: String(person.id), label: person.username }))}
          label={t('game.people.givesTo', { name: card.username })}
          placeholder={t('game.people.chooseGiftee')}
          onValueChange={(value) => {
            setWardId(value);
            onChange(Number(value));
          }}
          className="game-assignment-select"
          testId="game-assign-giftee"
        />
      ) : (
        <Typography as="p" role="body/base">
          {t('game.people.assignEmpty')}
        </Typography>
      )}
      {giftee && santa && (
        <Stack gap={4} className="game-assignment-preview" testId="game-assignment-preview">
          <Note tone="info">
            {t('game.people.assignmentChange', {
              giver: card.username,
              receiver: giftee.username,
              santa: santa.username,
            })}
          </Note>
          <GifteePair
            santa={card}
            giftee={giftee}
            santaLabel={t('box.organizer.giftee.santa')}
            gifteeLabel={t('box.organizer.giftee.giftee')}
            testId="game-assignment-new-pair"
          />
          <GifteePair
            santa={santa}
            giftee={card}
            santaLabel={t('box.organizer.giftee.santa')}
            gifteeLabel={t('box.organizer.giftee.giftee')}
            testId="game-assignment-reassigned-pair"
          />
        </Stack>
      )}
    </Stack>
  );
}

export function useParticipantSelection(
  info: BoxInfo,
  cards: CardInfo[],
  onChanged: () => Promise<void> | void,
) {
  const { t, i18n } = useTranslation();
  const [selection, setSelection] = useState({ key: info.box.key, ids: [] as number[] });
  const ids = selection.key === info.box.key ? selection.ids : [];
  function setIds(next: number[] | ((current: number[]) => number[])) {
    setSelection((current) => {
      const validIds =
        current.key === info.box.key
          ? current.ids.filter((id) => cards.some((card) => card.id === id))
          : [];
      return { key: info.box.key, ids: typeof next === 'function' ? next(validIds) : next };
    });
  }
  const selected = cards.filter((card) => ids.includes(card.id));
  const onlyUnassigned = Boolean(
    info.isActiveDraw && selected.length && selected.every((card) => !card.wardId),
  );
  const allowed = info.isAdmin && !info.box.isArchived;
  function fail(message?: string) {
    const key = message && i18n.exists(`errors.${message}`) ? `errors.${message}` : message;
    showToast(t(key && i18n.exists(key) ? key : 'game.draw.failed'));
    throw new Error(message || 'Participant operation failed');
  }
  return {
    selectedIds: selected.map((card) => card.id),
    onlyUnassigned,
    clear: () => setIds([]),
    toggleMany: (targets: number[], checked: boolean) =>
      setIds((current) =>
        checked
          ? [...new Set([...current, ...targets])]
          : current.filter((id) => !targets.includes(id)),
      ),
    draw: () => {
      if (!allowed || !onlyUnassigned || selected.length < 2) return;
      showDialog({
        title: t('game.people.additionalDraw'),
        children: t('game.people.additionalBody'),
        cancel: t('box.cancel'),
        submit: t('game.people.additionalDraw'),
        testId: 'game-additional-draw-dialog',
        onSubmit: async () => {
          try {
            randomize(
              selected.map((card) => ({
                id: String(card.id),
                exclusions: (card.exclusions || []).map(String),
              })),
            );
          } catch {
            fail('game.draw.failed');
          }
          const result = await santaClient.draw({
            key: info.box.key,
            cardIds: selected.map((card) => card.id),
          });
          if (!result.ok) fail(result.error?.message);
          setIds([]);
          await onChanged();
        },
      });
    },
    remove: () => {
      if (!allowed || !selected.length) return;
      if (
        info.isActiveDraw &&
        selected.some((card) => card.wardId) &&
        info.participantsNumber - selected.length < 2
      ) {
        showDialog({
          title: t(selected.length > 1 ? 'game.people.removeSelectedTitle' : 'game.people.removeTitle'),
          children: t('game.people.removeTooMany'),
          submit: t('box.close'),
          testId: 'game-delete-limit-dialog',
        });
        return;
      }
      // Keep successful deletions out of a retry if a later request fails.
      const pending = [...selected];
      showDialog({
        title: t(selected.length > 1 ? 'game.people.removeSelectedTitle' : 'game.people.removeTitle'),
        children: t('game.people.removeSelectedBody', {
          names: selected.map((card) => card.username).join(', '),
        }) + (selected.some((card) => (card.isYou && info.isAdmin) || info.organizers?.some((person) => person.id === card.id)) ? ' ' + t('game.people.removeOrganizerHint') : '') + (info.isActiveDraw && selected.some((card) => card.wardId) ? ' ' + t('game.people.removeAfterDrawHint') : ''),
        cancel: t('box.cancel'),
        submit: t('game.people.removeSelected'),
        submitVariant: 'danger',
        testId: 'game-delete-selected-dialog',
        onSubmit: async () => {
          try {
            while (pending.length) {
              const card = pending[0];
              const result = await santaClient.deleteOtherCard({ key: info.box.key, id: card.id });
              if (!result.ok) fail(result.error?.message);
              pending.shift();
              setIds((current) => current.filter((id) => id !== card.id));
            }
          } finally {
            await onChanged();
          }
        },
      });
    },
    assign: (card: CardInfo) => {
      if (!allowed || !info.isActiveDraw || card.wardId) return;
      let wardId = 0;
      const dialog = {
        title: t('game.people.assignTitle', { name: card.username }),
        children: (
          <Assignment
            card={card}
            cards={cards}
            onChange={(id) => {
              wardId = id;
              showDialog({ ...dialog, submitDisabled: !id });
            }}
          />
        ),
        cancel: t('box.cancel'),
        submit: t('game.people.saveAssignment'),
        submitDisabled: true,
        testId: 'game-assign-dialog',
        mobileFullScreen: true,
        onSubmit: async () => {
          if (!wardId) fail('game.people.chooseGiftee');
          const result = await santaClient.updateOtherCard({
            key: info.box.key,
            id: card.id,
            wardId,
          });
          if (!result.ok) fail(result.error?.message);
          await onChanged();
        },
      };
      showDialog(dialog);
    },
  };
}
